import React from 'react';
import { TYPE, cn } from './ui';

/**
 * Shared frame for the signed-out screens.
 *
 * The wordmark is small and the page's own title is the largest thing on it —
 * you know what app you opened; what you need to read is what this screen is
 * asking you for.
 */
export const AuthShell: React.FC<{
  title: string;
  subhead: string;
  error?: string;
  notice?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}> = ({ title, subhead, error, notice, children, footer }) => (
  <div className="min-h-screen bg-ground flex items-start sm:items-center justify-center px-5 sm:px-[44px] py-16">
    <div className="w-full max-w-[440px] bg-surface border border-rule-base p-8">
      <div className="flex items-center gap-2.5 mb-8">
        <span className="w-[15px] h-[15px] bg-accent rounded-xs" aria-hidden="true" />
        <span className="font-display text-[16px] font-semibold tracking-[-0.02em] text-ink">
          SmartySpend
        </span>
      </div>

      <h1 className={cn(TYPE.pageHeading, 'text-ink')}>{title}</h1>
      <p className={cn(TYPE.body, 'text-ink-soft mt-2')}>{subhead}</p>

      {/* An error is the one thing here that earns crimson; a notice is
          information, so it takes an ochre rule and no fill fighting the
          form beneath it. */}
      {error && (
        <div className="border-l-2 border-bad bg-bad-tint px-4 py-3 mt-6">
          <div className={cn(TYPE.tag, 'text-bad')}>Error</div>
          <p className={cn(TYPE.small, 'text-ink mt-1.5')}>{error}</p>
        </div>
      )}
      {notice && !error && (
        <div className="border-l-2 border-warn px-4 py-3 mt-6">
          <p className={cn(TYPE.small, 'text-ink-soft')}>{notice}</p>
        </div>
      )}

      <div className="mt-7">{children}</div>

      {footer && <div className="mt-7 pt-6 border-t border-rule">{footer}</div>}
    </div>
  </div>
);
